import { Button, Input, PasswordInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";

import Page from "@/components/Page.tsx";

import { AuthSignInStep } from "@/models/Auth.ts";

import { confirmSignInWithNewPassword, signIn } from "@/services/authService.ts";

interface LoginForm {
    email: string;
    password: string;
    newPassword: string;
}

const LoginPage: FC = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const form = useForm<LoginForm>({
        mode: "uncontrolled",
        validateInputOnBlur: true,

        validate: {
            email: (value) => (/^\S+@\S+$/.test(value) ? undefined : t("validation.invalidEmail")),
        },
    });

    const onSubmit = async (values: LoginForm): Promise<void> => {
        setLoading(true);
        try {
            const signInStep = await signIn(values.email, values.password);
            switch (signInStep) {
                case AuthSignInStep.CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED: {
                    setShowNewPassword(true);
                    break;
                }
                default: {
                    break;
                }
            }
        } catch (error) {
            console.debug(error);
            // TODO: handle error types
        }
        setLoading(false);
    };

    const onNewPasswordSubmit = async (values: LoginForm): Promise<void> => {
        if (!values.newPassword) return;

        setLoading(true);
        try {
            await confirmSignInWithNewPassword(values.newPassword);
        } catch (error) {
            console.debug(error);
            // TODO: handle error types
        }
        setLoading(false);
    };

    return (
        <Page className="justify-center gap-8" fullScreen>
            <form onSubmit={form.onSubmit(showNewPassword ? onNewPasswordSubmit : onSubmit)}>
                {!showNewPassword && (
                    <>
                        <Input.Wrapper label="Email">
                            <Input disabled={loading} {...form.getInputProps("email")} />
                        </Input.Wrapper>
                        <Input.Wrapper label="Password">
                            <PasswordInput disabled={loading} {...form.getInputProps("password")} />
                        </Input.Wrapper>
                    </>
                )}
                {showNewPassword && (
                    <Input.Wrapper label="New Password">
                        <PasswordInput disabled={loading} {...form.getInputProps("newPassword")} />
                    </Input.Wrapper>
                )}
                <Button loading={loading} type="submit">
                    {t("auth.signIn")}
                </Button>
            </form>
        </Page>
    );
};

export default LoginPage;
